import React, { useState } from "react";
import type { SendingDomainResponse } from "@common/schemas/sending-domain.schema";
import { SendingDomainStatus } from "@common/types/domains/emailSending";
import { CopyIcon } from "@icons";
import { Button } from "@design-system/Button/Button";
import { ButtonWithLoader } from "@design-system/Button/ButtonWithLoader";
import { CopyOnClick } from "@design-system/CopyOnClick/CopyOnClick";
import { HStack } from "@design-system/HStack/HStack";
import { StatusIndicator } from "@design-system/StatusIndicator/StatusIndicator";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import { VStack } from "@design-system/VStack/VStack";
import { useVerifySendingDomain } from "@api/sendingDomain.api";
import { useWorkspaceSendingOwner } from "@hooks/useWorkspaceSendingOwner";
import styles from "./SendingDomainsSettings.module.scss";

interface DnsRecordsPanelProps {
    domain: SendingDomainResponse;
    onVerified: () => void;
    onDismiss: () => void;
}

export const DnsRecordsPanel: React.FC<DnsRecordsPanelProps> = ({
    domain,
    onVerified,
    onDismiss,
}) => {
    const [recordsDomain, setRecordsDomain] = useState(domain);
    const owner = useWorkspaceSendingOwner();
    const verifyDomain = useVerifySendingDomain(owner);
    const { toast } = useToaster();
    const records = recordsDomain.dnsRecords ?? [];

    const handleVerify = async () => {
        try {
            const updated = await verifyDomain.mutateAsync(recordsDomain.id);
            setRecordsDomain(updated);
            if (updated.status === SendingDomainStatus.verified) {
                toast("Domain verified");
                onVerified();
            }
        } catch {
            // The API client already toasts the error body.
        }
    };

    return (
        <VStack gap={12}>
            <p className={styles.recordsIntro}>
                Add these records at your DNS provider for{" "}
                <strong>{recordsDomain.domain}</strong>, then verify. It can
                take a few minutes for DNS changes to propagate.
            </p>
            {recordsDomain.lastVerificationError && (
                <p className={styles.recordsIntro}>
                    {recordsDomain.lastVerificationError}
                </p>
            )}
            <table className={styles.recordsTable}>
                <thead>
                    <tr>
                        <th>Type</th>
                        <th>Host</th>
                        <th>Value</th>
                        <th>Status</th>
                    </tr>
                </thead>
                <tbody>
                    {records.map((record) => (
                        <tr key={`${record.type}-${record.host}`}>
                            <td>{record.type}</td>
                            <td>
                                <CopyOnClick
                                    value={record.host}
                                    className={styles.copyValue}
                                >
                                    <span className={styles.mono}>
                                        {record.host}
                                    </span>{" "}
                                    <CopyIcon className="ml-1" />
                                </CopyOnClick>
                            </td>
                            <td>
                                <CopyOnClick
                                    value={record.data}
                                    className={styles.copyValue}
                                >
                                    <span className={styles.mono}>
                                        {record.data}
                                    </span>{" "}
                                    <CopyIcon className="ml-1" />
                                </CopyOnClick>
                            </td>
                            <td>
                                <StatusIndicator
                                    appearance="solid"
                                    variant={record.valid ? "blue" : "yellow"}
                                    label={
                                        record.valid ? "Valid" : "Not yet valid"
                                    }
                                    showText
                                />
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
            <HStack justify="end" gap={12}>
                <Button
                    variant="secondary"
                    size="xl"
                    type="button"
                    onClick={onDismiss}
                    disabled={verifyDomain.isPending}
                >
                    I'll do this later
                </Button>
                <ButtonWithLoader
                    variant="primary"
                    size="xl"
                    type="button"
                    loading={verifyDomain.isPending}
                    onClick={() => {
                        void handleVerify();
                    }}
                >
                    Verify DNS
                </ButtonWithLoader>
            </HStack>
        </VStack>
    );
};
